import { Typography } from '@mui/material';
import { useState } from 'react';

import { useCreateMailpiecesListCommentMutation, useUpdatePiecesMutation } from '@api/piece';
import { PieceAction } from '@apiContract';

import { clearRecipientFilter } from '@redux/slices/recipientFilter';
import { useDispatch, useSelector } from '@redux/store';

import { ActionComponentProps } from '../../index';
import { getItemsCount } from '../../utils';
import { DialogQrCodeConfirmation } from '../DialogQrCodeConfirmation';
import { ActionWithInfo } from './ActionWithInfo';

export const FinishDeliveryWithQrCode = ({ mailpieces, actionStarted, onActionFinish }: ActionComponentProps) => {
  const dispatch = useDispatch();
  const [updatePieces, { isLoading }] = useUpdatePiecesMutation();
  const [createMailpiecesListComment] = useCreateMailpiecesListCommentMutation();
  const [actionInProgress, setActionInProgress] = useState(false);

  const { qrCode: prefilledQrCode } = useSelector((state) => state.recipientFilter);

  const count = getItemsCount(mailpieces);

  const confirmAction = async (
    qrCodeData: string,
    skipNotifyRecipient: boolean,
    comment: string,
    notifyMailroom: boolean,
  ) => {
    setActionInProgress(true);

    try {
      await updatePieces({
        pieces: mailpieces.map((piece) => ({ id: piece.id })),
        action: PieceAction.FinishDeliveryWithQrCode,
        qrCode: qrCodeData,
        shouldNotifyRecipient: !skipNotifyRecipient,
      }).unwrap();

      if (comment.trim().length) {
        await createMailpiecesListComment({
          text: comment.trim(),
          notifyMailroom,
          pieceIds: mailpieces.map((piece) => piece.id),
        }).unwrap();
      }

      dispatch(clearRecipientFilter());
    } finally {
      setActionInProgress(false);
    }
  };

  return (
    <ActionWithInfo
      open={actionStarted}
      title={prefilledQrCode ? (!count ? 'Deliver item' : 'Deliver selected items') : 'Complete with QR Code'}
      onClose={onActionFinish}
      acceptButtonTitle={prefilledQrCode ? 'Complete' : 'Scan QR Code'}
      loading={isLoading || actionInProgress}
      onAccept={
        prefilledQrCode
          ? async ({ skipNotifyRecipient, comment, notifyMailroom }) => {
              try {
                await confirmAction(prefilledQrCode, skipNotifyRecipient, comment, notifyMailroom);
                onActionFinish();
              } catch {
                dispatch(clearRecipientFilter());
              }
            }
          : undefined
      }
      infoContent={
        <Typography variant="body1" color="text.secondary">
          {prefilledQrCode ? (
            !count ? (
              <>Please deliver this item to the recipient, and click Complete when you&apos;re done.</>
            ) : (
              <>
                Please deliver <strong>{mailpieces.length}</strong> items to the recipient, and click Complete when
                you&apos;re done.
              </>
            )
          ) : count > 1 ? (
            `Please scan the recipient’s QR code for ${count} items.`
          ) : (
            'Please scan the recipient’s QR code for this item.'
          )}
        </Typography>
      }
    >
      {({
        step,
        skipNotifyRecipient,
        comment,
        notifyMailroom,
        onClose,
        onBackToInfo,
      }) => (
        <DialogQrCodeConfirmation
          mailpieces={mailpieces}
          actionStarted={actionStarted && step === 'action'}
          actionRequestInProgress={isLoading || actionInProgress}
          onActionFinish={onClose}
          onQrCodeRecognized={async (qrCodeData) => {
            try {
              await confirmAction(qrCodeData, skipNotifyRecipient, comment, notifyMailroom);
              onClose();
            } catch {
              onBackToInfo('The scanned QR code does not match the recipient');
            }
          }}
        />
      )}
    </ActionWithInfo>
  );
};
